import html2canvas from 'html2canvas';
import * as echarts from 'echarts';

export const PDF_EXPORT_SCALE = 2;
export const IMAGE_EXPORT_SCALE = 3;

export const EXPORT_CAPTURE_CLASS = 'leadership-export-capture-active';
export const DASHBOARD_EXPORT_MODE_CLASS = 'dashboard-export-mode';

const EXPORT_MIN_VIEWPORT_WIDTH = 1200;
const CHART_RENDER_WAIT_MS = 250;
const LAYOUT_SETTLE_MS = 100;
const LAYOUT_STABILITY_ATTEMPTS = 5;

const wait = (ms) => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

const waitForNextFrame = () => new Promise((resolve) => {
  requestAnimationFrame(() => {
    requestAnimationFrame(resolve);
  });
});

const isHTMLElement = (node) => node instanceof HTMLElement;

const collectExportNodes = (root) => {
  if (!isHTMLElement(root)) {
    return [];
  }
  return [root, ...Array.from(root.querySelectorAll('*')).filter(isHTMLElement)];
};

export const getCaptureDimensions = (element) => {
  if (!isHTMLElement(element)) {
    return { width: 0, height: 0, viewportWidth: EXPORT_MIN_VIEWPORT_WIDTH };
  }

  const width = Math.max(element.scrollWidth, element.offsetWidth);
  const height = Math.max(element.scrollHeight, element.offsetHeight);
  const viewportWidth = Math.max(width, EXPORT_MIN_VIEWPORT_WIDTH);

  return { width, height, viewportWidth };
};

export const measureFullElementDimensions = (element) => {
  if (!isHTMLElement(element)) {
    return { width: 0, height: 0 };
  }

  const { width, height } = getCaptureDimensions(element);
  const rect = element.getBoundingClientRect();
  let maxBottom = rect.top + height;
  let maxRight = rect.left + width;

  element.querySelectorAll('*').forEach((child) => {
    if (!isHTMLElement(child)) {
      return;
    }

    const childRect = child.getBoundingClientRect();
    if (childRect.width <= 0 && childRect.height <= 0) {
      return;
    }

    maxBottom = Math.max(maxBottom, childRect.bottom);
    maxRight = Math.max(maxRight, childRect.right);
  });

  return {
    width: Math.ceil(Math.max(width, maxRight - rect.left)),
    height: Math.ceil(Math.max(height, maxBottom - rect.top)),
  };
};

let dashboardExportModeCount = 0;

export const enableDashboardExportMode = () => {
  dashboardExportModeCount += 1;
  document.body.classList.add(DASHBOARD_EXPORT_MODE_CLASS);
};

export const disableDashboardExportMode = () => {
  dashboardExportModeCount = Math.max(0, dashboardExportModeCount - 1);
  if (dashboardExportModeCount === 0) {
    document.body.classList.remove(DASHBOARD_EXPORT_MODE_CLASS);
  }
};

const createStyleSnapshot = (node) => ({
  node,
  overflow: node.style.overflow,
  overflowX: node.style.overflowX,
  overflowY: node.style.overflowY,
  maxHeight: node.style.maxHeight,
  minHeight: node.style.minHeight,
  minWidth: node.style.minWidth,
  height: node.style.height,
  width: node.style.width,
  alignSelf: node.style.alignSelf,
  flex: node.style.flex,
  hadCaptureClass: node.classList.contains(EXPORT_CAPTURE_CLASS),
});

const applyExportSafeStyles = (root) => {
  if (!isHTMLElement(root)) {
    return [];
  }

  const snapshots = collectExportNodes(root).map(createStyleSnapshot);
  root.classList.add(EXPORT_CAPTURE_CLASS);

  collectExportNodes(root).forEach((node) => {
    node.style.overflow = 'visible';
    node.style.overflowX = 'visible';
    node.style.overflowY = 'visible';
    node.style.maxHeight = 'none';
    node.style.minHeight = 'auto';
    node.style.minWidth = 'auto';
  });

  root.style.height = 'auto';
  root.style.minHeight = 'auto';
  root.style.maxHeight = 'none';
  root.style.overflow = 'visible';
  root.style.alignSelf = 'start';
  root.style.flex = 'none';

  const cardWidth = Math.max(root.scrollWidth, root.offsetWidth);
  if (cardWidth > 0) {
    root.style.width = `${cardWidth}px`;
    root.style.minWidth = `${cardWidth}px`;
  }

  collectExportNodes(root).forEach((node) => {
    const expandedHeight = Math.max(node.scrollHeight, node.offsetHeight, node.clientHeight);
    if (expandedHeight > node.clientHeight + 1) {
      node.style.height = `${expandedHeight}px`;
      node.style.minHeight = `${expandedHeight}px`;
    }
  });

  return snapshots;
};

const restoreStyleSnapshots = (snapshots) => {
  snapshots.forEach(
    ({
      node,
      overflow,
      overflowX,
      overflowY,
      maxHeight,
      minHeight,
      minWidth,
      height,
      width,
      alignSelf,
      flex,
      hadCaptureClass,
    }) => {
      node.style.overflow = overflow;
      node.style.overflowX = overflowX;
      node.style.overflowY = overflowY;
      node.style.maxHeight = maxHeight;
      node.style.minHeight = minHeight;
      node.style.minWidth = minWidth;
      node.style.height = height;
      node.style.width = width;
      node.style.alignSelf = alignSelf;
      node.style.flex = flex;

      if (hadCaptureClass) {
        node.classList.add(EXPORT_CAPTURE_CLASS);
      } else {
        node.classList.remove(EXPORT_CAPTURE_CLASS);
      }
    }
  );
};

const applyExportModeToClone = (clonedDocument, clonedTarget) => {
  clonedDocument.body?.classList.add(DASHBOARD_EXPORT_MODE_CLASS);
  applyExportSafeStyles(clonedTarget);
  clonedTarget.style.boxShadow = 'none';
};

const resizeChartsInElement = (element) => {
  if (!isHTMLElement(element)) {
    return;
  }

  element.querySelectorAll('div[_echarts_instance_]').forEach((chartNode) => {
    const instance = echarts.getInstanceByDom(chartNode);
    instance?.resize();
  });
};

const waitForStableLayout = async (element) => {
  let previous = { width: 0, height: 0 };

  for (let attempt = 0; attempt < LAYOUT_STABILITY_ATTEMPTS; attempt += 1) {
    await waitForNextFrame();
    const measured = measureFullElementDimensions(element);

    if (
      attempt > 0 &&
      measured.width === previous.width &&
      measured.height === previous.height
    ) {
      return measured;
    }

    previous = measured;

    if (attempt < LAYOUT_STABILITY_ATTEMPTS - 1) {
      // eslint-disable-next-line no-await-in-loop
      await wait(LAYOUT_SETTLE_MS);
    }
  }

  return measureFullElementDimensions(element);
};

const resolveClonedExportTarget = (clonedDocument, clonedElement, sourceElement) => {
  if (clonedElement?.id === sourceElement.id) {
    return clonedElement;
  }

  return clonedDocument.getElementById(sourceElement.id) || clonedElement;
};

const renderExportCanvas = async (element, scale, measured) => {
  const viewportWidth = Math.max(measured.width, EXPORT_MIN_VIEWPORT_WIDTH);
  const viewportHeight = measured.height;

  const html2canvasOptions = {
    scale,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
    width: measured.width,
    height: measured.height,
    windowWidth: viewportWidth,
    windowHeight: viewportHeight,
    scrollX: 0,
    scrollY: 0,
    onclone: (clonedDocument, clonedElement) => {
      const clonedTarget = resolveClonedExportTarget(clonedDocument, clonedElement, element);

      if (!isHTMLElement(clonedTarget)) {
        return;
      }

      applyExportModeToClone(clonedDocument, clonedTarget);
    },
  };

  let canvas = await html2canvas(element, html2canvasOptions);
  const minExpectedHeight = measured.height * scale * 0.92;
  const minExpectedWidth = measured.width * scale * 0.92;

  if (canvas.height < minExpectedHeight || canvas.width < minExpectedWidth) {
    const remeasured = measureFullElementDimensions(element);
    canvas = await html2canvas(element, {
      ...html2canvasOptions,
      width: remeasured.width,
      height: remeasured.height,
      windowWidth: Math.max(remeasured.width, EXPORT_MIN_VIEWPORT_WIDTH),
      windowHeight: remeasured.height,
      onclone: (clonedDocument, clonedElement) => {
        const clonedTarget = resolveClonedExportTarget(clonedDocument, clonedElement, element);

        if (!isHTMLElement(clonedTarget)) {
          return;
        }

        applyExportModeToClone(clonedDocument, clonedTarget);
      },
    });
  }

  return canvas;
};

export const waitForExportCards = async (selectedItems, timeoutMs = 8000) => {
  const startedAt = Date.now();

  while (Date.now() - startedAt < timeoutMs) {
    const allPresent = selectedItems.every((item) => Boolean(document.getElementById(item.id)));
    if (allPresent) {
      return true;
    }
    // eslint-disable-next-line no-await-in-loop
    await wait(100);
  }

  return selectedItems.every((item) => Boolean(document.getElementById(item.id)));
};

export const prepareDomForExport = async () => {
  window.dispatchEvent(new Event('resize'));
  await waitForNextFrame();

  document.querySelectorAll('[id^="export-"]').forEach((element) => {
    resizeChartsInElement(element);
  });

  await wait(CHART_RENDER_WAIT_MS);
};

export const captureExportElement = async (element, scale = PDF_EXPORT_SCALE) => {
  if (!isHTMLElement(element)) {
    throw new Error('Export target element was not found.');
  }

  element.scrollIntoView({ block: 'start', behavior: 'auto' });
  await waitForNextFrame();

  resizeChartsInElement(element);
  await wait(CHART_RENDER_WAIT_MS);

  const styleSnapshots = applyExportSafeStyles(element);

  try {
    await waitForNextFrame();
    await wait(LAYOUT_SETTLE_MS);

    const measured = await waitForStableLayout(element);
    return renderExportCanvas(element, scale, measured);
  } finally {
    restoreStyleSnapshots(styleSnapshots);
  }
};

export const canvasToBlob = (canvas, type = 'image/png', quality = 1) =>
  new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });

export const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
